/**
 * <ArchivedActivitiesPage> — « Activités archivées » (page).
 *
 * Ce que le legacy montre (`ArchivedActivitiesScreen.js`) : les activités mises de côté, une
 * sélection multiple (appui long ou « Sélectionner »), et la barre « Restaurer (N) » qui les
 * remet dans la liste à faire. Le rangement vide porte un glyphe, pas un champion : une liste
 * d'archives ne demande rien à personne.
 *
 * Les activités tiennent dans UNE carte-liste, et la suite se demande par « Voir plus » en pied
 * de carte — l'écran legacy charge par vingt. Contrôlée de bout en bout : la sélection, la
 * pagination et l'envoi arrivent en props.
 */
import React from "react"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { ActivityRow } from "../../molecules/ActivityRow"
import { ActivityRowSkeleton } from "../../molecules/ActivityRow/ActivityRowSkeleton"
import { CardList, CardListLinkRow } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { Header } from "../../molecules/Header"
import { RefreshButton } from "../../molecules/RefreshButton"
import { SelectionActionBar } from "../../molecules/SelectionActionBar"
import { SelectionHeaderRow } from "../../molecules/SelectionHeaderRow"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { type UIActivity } from "../../../ports/primary/ui/types"
import { ArchivedActivitiesSkeleton } from "./ArchivedActivitiesSkeleton"

export type ArchivedActivitiesPageProps = {
    activities: UIActivity[]
    /** Toucher une activité ouvre sa fiche (hors sélection multiple). */
    onOpenActivity?: (id: UIActivity["id"]) => void
    onClose?: () => void
    /** Un rafraîchissement est en cours : le bouton de l'en-tête tourne. */
    refreshing?: boolean
    onRefresh?: () => void

    // ── Sélection multiple (contrôlée) ──
    selectionMode?: boolean
    selectedIds?: Array<UIActivity["id"]>
    onStartSelection?: (id?: UIActivity["id"]) => void
    onToggleSelect?: (id: UIActivity["id"]) => void
    onToggleSelectAll?: () => void
    onCancelSelection?: () => void
    onRestoreSelected?: () => void
    /** La restauration est partie : la barre tourne et ne répond plus. */
    restoring?: boolean

    // ── Suite de la liste (l'écran legacy charge par vingt) ──
    hasMore?: boolean
    loadingMore?: boolean
    onLoadMore?: () => void

    // ── État de page ──
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function ArchivedActivitiesPage(props: ArchivedActivitiesPageProps) {
    const {
        activities,
        onOpenActivity,
        onClose,
        refreshing = false,
        onRefresh,
        selectionMode = false,
        selectedIds = [],
        onStartSelection,
        onToggleSelect,
        onToggleSelectAll,
        onCancelSelection,
        onRestoreSelected,
        restoring = false,
        hasMore = false,
        loadingMore = false,
        onLoadMore,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    const isEmpty = activities.length === 0
    const selectedCount = selectedIds.length
    const allSelected = !isEmpty && selectedCount === activities.length

    const pressRow = (activity: UIActivity) => {
        if (selectionMode) onToggleSelect?.(activity.id)
        else onOpenActivity?.(activity.id)
    }

    const header = (
        <Header
            title="Activités archivées"
            left={
                onClose != null ? (
                    <IconButton
                        iconName="times"
                        appearance="transparent"
                        iconColor="primary"
                        onPress={onClose}
                        accessibilityLabel="Fermer"
                    />
                ) : undefined
            }
            right={onRefresh != null ? <RefreshButton refreshing={refreshing} onRefresh={onRefresh} /> : undefined}
        />
    )

    return (
        <PageTemplate
            refreshing={refreshing}
            onRefresh={onRefresh}
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<ArchivedActivitiesSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={PARENT_CONTENT_WIDTH}
            footer={
                selectionMode ? (
                    <SelectionActionBar
                        count={selectedCount}
                        actionLabel="Restaurer"
                        iconName="undo-alt"
                        onAction={onRestoreSelected}
                        loading={restoring}
                    />
                ) : undefined
            }
        >
            {isEmpty ? (
                <EmptyState iconName="archive" title="Aucune activité archivée" />
            ) : (
                <Stack gap="3">
                    <SelectionHeaderRow
                        selectionMode={selectionMode}
                        selectedCount={selectedCount}
                        allSelected={allSelected}
                        onCancelSelection={onCancelSelection}
                        onToggleSelectAll={onToggleSelectAll}
                        onStartSelection={onStartSelection == null ? undefined : () => onStartSelection()}
                    >
                        <Text size="text-md" color="text-muted">
                            {activities.length} activité{activities.length > 1 ? "s" : ""} de côté
                        </Text>
                    </SelectionHeaderRow>

                    <CardList>
                        {activities.map((activity) => (
                            <ActivityRow
                                key={activity.id}
                                activity={activity}
                                selectable={selectionMode}
                                selected={selectedIds.includes(activity.id)}
                                onPress={pressRow}
                                onLongPress={
                                    selectionMode || onStartSelection == null
                                        ? undefined
                                        : (item) => onStartSelection(item.id)
                                }
                            />
                        ))}
                        {loadingMore ? (
                            // La suite arrive : deux lignes de sa forme, sous un seul balayage.
                            <DelayedSkeleton>
                                <ActivityRowSkeleton titleWidth={160} />
                                <ActivityRowSkeleton titleWidth={120} divided />
                            </DelayedSkeleton>
                        ) : hasMore && onLoadMore != null ? (
                            <CardListLinkRow label="Voir plus" onPress={onLoadMore} />
                        ) : null}
                    </CardList>
                </Stack>
            )}
        </PageTemplate>
    )
}
